import axios from 'axios';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { toast } from 'react-toastify';
import { api } from '../../Api';
import { Store } from '../../Store';
import { getError } from '../../utils';

/**
 * REMOTE CONTROL — the switches that reach every phone at once.
 *
 * Which backend the app talks to, whether the app is open for business, and
 * whether everyone must update. Each one is rare to change and quick to
 * regret, so each states its consequence in a full sentence and asks again
 * before it goes through.
 *
 * The server switch stores a KEY, never a URL. The app carries its own list
 * of hosts and ignores anything not on it, so this page cannot point a single
 * phone at a server we do not own.
 */

const SERVERS = [
  {
    key: 'testing',
    label: 'Testing',
    host: 'testing.iconmarketing.shop',
    note: 'Practice data. Anything done here does not count — but OTP messages it sends are real.',
    colour: '#fd7e14',
  },
  {
    key: 'production',
    label: 'Production',
    host: 'update.iconmarketing.shop',
    note: 'The live business. Real members, real money.',
    colour: '#198754',
  },
];

const card = {
  background: '#fff',
  border: '1px solid #e5e7eb',
  borderRadius: 12,
  padding: 18,
  marginBottom: 18,
};
const label = { display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6, color: '#374151' };
const input = {
  width: '100%',
  padding: '10px 12px',
  border: '1px solid #d1d5db',
  borderRadius: 8,
  fontSize: 14,
  marginBottom: 14,
};
const button = (bg, disabled) => ({
  padding: '11px 16px',
  borderRadius: 9,
  border: 'none',
  background: disabled ? '#9ca3af' : bg,
  color: '#fff',
  fontWeight: 600,
  fontSize: 14,
  cursor: disabled ? 'default' : 'pointer',
});
const smallButton = {
  padding: '6px 10px',
  borderRadius: 7,
  border: '1px solid #d1d5db',
  background: '#f3f4f6',
  color: '#111827',
  fontWeight: 600,
  fontSize: 12,
  cursor: 'pointer',
};
const subCard = { border: '1px solid #eef0f3', borderRadius: 10, padding: 12, marginBottom: 12, background: '#fafafa' };

const pad2 = (n) => String(n).padStart(2, '0');

/** ISO (UTC) -> datetime-local value in the admin's own timezone. */
function isoToLocalInput(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

/** datetime-local value (local, no zone) -> ISO (UTC). */
function localInputToIso(value) {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

const MAX_MAINTENANCE_BUTTONS = 3;
const MAX_SLIDES = 8;
const MAX_TILES = 24;
const TILE_ROLES = ['member', 'agent', 'manager', 'admin'];

// The app's built-in member dashboard (src/store/config.ts DEFAULT_HOME_TILES),
// as a starting point. The server list replaces it entirely once non-empty.
const APP_DEFAULT_TILES = [
  { key: 'treeview', label: 'Team Maintenance', icon: 'file-tree-outline', route: '/member/team', color: '#2563eb', order: 1, badge: '', control: 'member.team', roles: [] },
  { key: 'transection', label: 'Transaction', icon: 'swap-vertical-bold', route: '/member/wallet', color: '#7c3aed', order: 2, badge: '', control: 'member.wallet', roles: [] },
  { key: 'transfer', label: 'Transfer & History', icon: 'bank-transfer', route: '/member/wallet/transfer', color: '#0891b2', order: 3, badge: '', control: 'member.wallet.transfer', roles: [] },
  { key: 'shop', label: 'Shop', icon: 'storefront-outline', route: '/member/shop', color: '#d97706', order: 4, badge: '', control: 'member.shop', roles: [] },
  { key: 'promotion', label: 'Sales man Promotion', icon: 'trophy-outline', route: '/member/promotion/status', color: '#db2777', order: 5, badge: '', control: 'member.promotion.status', roles: [] },
  { key: 'withdraw', label: 'Withdraw', icon: 'bank-outline', route: '/member/wallet/withdraw', color: '#16a34a', order: 6, badge: '', control: 'member.wallet.withdraw', roles: [] },
  { key: 'sales', label: 'Sales', icon: 'chart-line', route: '/member/sales', color: '#0d9488', order: 7, badge: '', control: 'member.sales.mine', roles: [] },
];

const moveItem = (list, from, to) => {
  if (to < 0 || to >= list.length) return list;
  const next = list.slice();
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
};

export default function RemoteControlScreen() {
  const { state } = useContext(Store);
  const { userInfo } = state;
  const headers = useMemo(
    () => ({ Authorization: `Bearer ${userInfo.token}` }),
    [userInfo.token]
  );

  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(api + '/api/app/config', { headers });
      setCfg(data);
    } catch (error) {
      toast.error(getError(error));
    }
  }, [headers]);

  useEffect(() => {
    load();
  }, [load]);

  const patch = (path, value) => {
    setCfg((prev) => {
      if (!prev) return prev;
      const next = JSON.parse(JSON.stringify(prev));
      const parts = path.split('.');
      let cur = next;
      for (let i = 0; i < parts.length - 1; i += 1) {
        cur[parts[i]] = cur[parts[i]] || {};
        cur = cur[parts[i]];
      }
      cur[parts[parts.length - 1]] = value;
      return next;
    });
  };

  const save = async (body, successMessage) => {
    setSaving(true);
    try {
      const { data } = await axios.put(api + '/api/app/config', body, { headers });
      // Take back only the slice that was saved, so unsaved edits in the
      // other sections of this page survive.
      if (data?.config) {
        setCfg((prev) => {
          const next = { ...(prev || {}) };
          for (const section of Object.keys(body)) {
            const fresh = data.config[section];
            const sent = body[section];
            if (Array.isArray(fresh) || Array.isArray(sent) || !fresh || typeof fresh !== 'object') {
              next[section] = fresh;
            } else {
              const merged = { ...(next[section] || {}) };
              for (const k of Object.keys(sent || {})) merged[k] = fresh[k];
              if (section === 'server') {
                merged.active = fresh.active;
                merged.switchedBy = fresh.switchedBy;
                merged.switchedAt = fresh.switchedAt;
              }
              next[section] = merged;
            }
          }
          next.version = data.config.version;
          return next;
        });
      }
      toast.success(successMessage || 'Saved.');
    } catch (error) {
      toast.error(getError(error));
    } finally {
      setSaving(false);
    }
  };

  if (!cfg) {
    return (
      <div className="container" style={{ maxWidth: 900, padding: 24 }}>
        <Helmet>
          <title>Remote control</title>
        </Helmet>
        Loading…
      </div>
    );
  }

  const activeServer = cfg.server?.active || 'testing';
  const maintenanceOn = Boolean(cfg.maintenance?.enabled);
  const mButtons = Array.isArray(cfg.maintenance?.buttons) ? cfg.maintenance.buttons : [];
  const setButton = (i, field, value) =>
    patch('maintenance.buttons', mButtons.map((b, j) => (j === i ? { ...b, [field]: value } : b)));
  const slides = Array.isArray(cfg.onboarding) ? cfg.onboarding : [];
  const setSlide = (i, field, value) =>
    patch('onboarding', slides.map((sl, j) => (j === i ? { ...sl, [field]: value } : sl)));
  const tiles = Array.isArray(cfg.home?.tiles) ? cfg.home.tiles : [];
  const maintenanceBody = () => ({
    title: cfg.maintenance?.title || '',
    message: cfg.maintenance?.message || '',
    imageUrl: cfg.maintenance?.imageUrl || '',
    until: cfg.maintenance?.until || null,
    buttons: mButtons,
  });
  const setTile = (i, field, value) =>
    patch('home.tiles', tiles.map((t, j) => (j === i ? { ...t, [field]: value } : t)));

  const switchServer = (s) => {
    const ok = window.confirm(
      `Move EVERY phone to the ${s.label} server?\n\n${s.note}\n\n`
        + 'The website is not affected — only the mobile app.'
    );
    if (!ok) return;
    save({ server: { active: s.key } }, `Apps will move to ${s.label} on their next load.`);
  };

  const toggleMaintenance = (on) => {
    const ok = window.confirm(
      on
        ? 'Close the app to members?\n\nEvery member sees the maintenance page instead of the app. '
          + 'Admins keep working so you can turn it back off.'
        : 'Open the app again?\n\nMembers get back in straight away.'
    );
    if (!ok) return;
    save(
      { maintenance: { ...maintenanceBody(), enabled: on } },
      on ? 'The app is now closed to members.' : 'The app is open again.'
    );
  };

  const toggleForceUpdate = (on) => {
    const ok = window.confirm(
      on
        ? 'Force everyone to update?\n\nNobody can use the app until they install a new version '
          + 'from the store. Make sure that new version is actually published first.'
        : 'Turn the update screen off?'
    );
    if (!ok) return;
    save(
      { app: { forceUpdate: on } },
      on ? 'Everyone now sees the update screen.' : 'Update screen turned off.'
    );
  };

  const logoutEveryone = () => {
    const ok = window.confirm(
      'Sign everyone out?\n\nEvery phone is signed out immediately and must log in again.'
    );
    if (!ok) return;
    save(
      { app: { logoutAllBefore: new Date().toISOString() } },
      'Everyone has been signed out.'
    );
  };

  return (
    <div className="container" style={{ maxWidth: 900, paddingBottom: 60 }}>
      <Helmet>
        <title>Remote control</title>
      </Helmet>

      <h3 style={{ margin: '18px 0 4px' }}>Remote control</h3>
      <p style={{ color: '#6b7280', marginBottom: 18 }}>
        These switches reach every phone running the mobile app. The website is not affected.
      </p>

      {/* -------------------------------------------------------- server */}
      <div style={card}>
        <b style={{ fontSize: 15 }}>Which server the app uses</b>
        <p style={{ color: '#6b7280', fontSize: 13, margin: '4px 0 14px' }}>
          The app only ever accepts these two. It cannot be sent anywhere else, even by
          someone who gets into this panel.
        </p>

        {SERVERS.map((s) => {
          const live = activeServer === s.key;
          return (
            <div
              key={s.key}
              style={{
                display: 'flex',
                gap: 14,
                alignItems: 'center',
                padding: 14,
                marginBottom: 10,
                borderRadius: 10,
                border: `2px solid ${live ? s.colour : '#e5e7eb'}`,
                background: live ? `${s.colour}0d` : '#fff',
              }}
            >
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, color: live ? s.colour : '#111827' }}>
                  {s.label} {live ? '· in use' : ''}
                </div>
                <div style={{ fontSize: 12, color: '#6b7280' }}>{s.host}</div>
                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>{s.note}</div>
              </div>
              <button
                type="button"
                disabled={live || saving}
                onClick={() => switchServer(s)}
                style={button(s.colour, live || saving)}
              >
                {live ? 'In use' : `Switch to ${s.label}`}
              </button>
            </div>
          );
        })}

        {cfg.server?.switchedBy ? (
          <div style={{ fontSize: 12, color: '#6b7280' }}>
            Last changed by {cfg.server.switchedBy}
            {cfg.server.switchedAt ? ` on ${new Date(cfg.server.switchedAt).toLocaleString()}` : ''}
          </div>
        ) : null}

        <hr style={{ margin: '16px 0', border: 0, borderTop: '1px solid #f3f4f6' }} />
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 200px' }}>
            <span style={label}>Badge label shown in the app</span>
            <input
              style={input}
              maxLength={30}
              value={cfg.server?.label || ''}
              onChange={(e) => patch('server.label', e.target.value)}
              placeholder="e.g. TEST"
            />
          </div>
          <div style={{ flex: '2 1 280px' }}>
            <span style={label}>Note</span>
            <input
              style={input}
              maxLength={200}
              value={cfg.server?.note || ''}
              onChange={(e) => patch('server.note', e.target.value)}
              placeholder="Why the app is on this server"
            />
          </div>
        </div>
        <button
          type="button"
          onClick={() =>
            save(
              { server: { label: cfg.server?.label || '', note: cfg.server?.note || '' } },
              'Server label and note saved.'
            )
          }
          disabled={saving}
          style={button('#0d6efd', saving)}
        >
          Save label and note
        </button>
      </div>

      {/* --------------------------------------------------- maintenance */}
      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ flex: 1 }}>
            <b style={{ fontSize: 15 }}>Maintenance page</b>
            <div style={{ fontSize: 13, color: maintenanceOn ? '#dc2626' : '#6b7280' }}>
              {maintenanceOn ? 'Members are locked out right now' : 'The app is open'}
            </div>
          </div>
          <button
            type="button"
            onClick={() => toggleMaintenance(!maintenanceOn)}
            disabled={saving}
            style={button(maintenanceOn ? '#198754' : '#dc3545', saving)}
          >
            {maintenanceOn ? 'Open the app' : 'Close the app'}
          </button>
        </div>

        <hr style={{ margin: '16px 0', border: 0, borderTop: '1px solid #f3f4f6' }} />

        <span style={label}>Heading</span>
        <input
          style={input}
          value={cfg.maintenance?.title || ''}
          onChange={(e) => patch('maintenance.title', e.target.value)}
          placeholder="We will be right back"
        />

        <span style={label}>What to tell them</span>
        <textarea
          style={{ ...input, minHeight: 90, resize: 'vertical' }}
          value={cfg.maintenance?.message || ''}
          onChange={(e) => patch('maintenance.message', e.target.value)}
        />

        <span style={label}>Picture or GIF address</span>
        <input
          style={input}
          value={cfg.maintenance?.imageUrl || ''}
          onChange={(e) => patch('maintenance.imageUrl', e.target.value)}
          placeholder="https://…  (leave blank for the app's own artwork)"
        />

        <span style={label}>Expected back at</span>
        <input
          style={input}
          type="datetime-local"
          value={isoToLocalInput(cfg.maintenance?.until)}
          onChange={(e) => patch('maintenance.until', localInputToIso(e.target.value))}
        />

        <span style={label}>Buttons on the maintenance page (up to {MAX_MAINTENANCE_BUTTONS})</span>
        {mButtons.map((b, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <div key={i} style={subCard}>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 180px' }}>
                <span style={label}>Button {i + 1} text</span>
                <input
                  style={input}
                  value={b.label || ''}
                  maxLength={30}
                  onChange={(e) => setButton(i, 'label', e.target.value)}
                  placeholder="Call support"
                />
              </div>
              <div style={{ flex: '1 1 220px' }}>
                <span style={label}>Opens</span>
                <input
                  style={input}
                  value={b.url || ''}
                  onChange={(e) => setButton(i, 'url', e.target.value)}
                  placeholder="tel:+8801700000000 or https://…"
                />
              </div>
              <div style={{ flex: '1 1 160px' }}>
                <span style={label}>Icon</span>
                <input
                  style={input}
                  value={b.icon || ''}
                  onChange={(e) => setButton(i, 'icon', e.target.value)}
                  placeholder="information-outline"
                />
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                <input type="checkbox" checked={Boolean(b.primary)} onChange={(e) => setButton(i, 'primary', e.target.checked)} />
                Main (filled) button
              </label>
              <button type="button" style={smallButton} onClick={() => patch('maintenance.buttons', moveItem(mButtons, i, i - 1))} disabled={i === 0}>Up</button>
              <button type="button" style={smallButton} onClick={() => patch('maintenance.buttons', moveItem(mButtons, i, i + 1))} disabled={i === mButtons.length - 1}>Down</button>
              <button type="button" style={{ ...smallButton, color: '#dc2626' }} onClick={() => patch('maintenance.buttons', mButtons.filter((_, j) => j !== i))}>Remove</button>
            </div>
          </div>
        ))}
        {mButtons.length < MAX_MAINTENANCE_BUTTONS ? (
          <button
            type="button"
            style={{ ...smallButton, marginBottom: 14 }}
            onClick={() =>
              patch('maintenance.buttons', [
                ...mButtons,
                { label: '', url: '', icon: mButtons.length ? 'information-outline' : 'headset', primary: !mButtons.length },
              ])
            }
          >
            + Add a button
          </button>
        ) : null}
        <p style={{ fontSize: 12, color: '#6b7280', margin: '0 0 14px' }}>
          Icons are Material Community Icons names (e.g. headset, phone, whatsapp, web). A button with no text is dropped on save.
        </p>

        <button
          type="button"
          onClick={() =>
            save(
              { maintenance: maintenanceBody() },
              'Maintenance page saved.'
            )
          }
          disabled={saving}
          style={button('#0d6efd', saving)}
        >
          Save the maintenance page
        </button>
      </div>

      {/* -------------------------------------------------------- updates */}
      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ flex: 1 }}>
            <b style={{ fontSize: 15 }}>App updates</b>
            <div style={{ fontSize: 13, color: cfg.app?.forceUpdate ? '#dc2626' : '#6b7280' }}>
              {cfg.app?.forceUpdate
                ? 'Forced — nobody can use the app until they update'
                : 'Optional'}
            </div>
          </div>
          <button
            type="button"
            onClick={() => toggleForceUpdate(!cfg.app?.forceUpdate)}
            disabled={saving}
            style={button(cfg.app?.forceUpdate ? '#198754' : '#dc3545', saving)}
          >
            {cfg.app?.forceUpdate ? 'Stop forcing' : 'Force an update'}
          </button>
        </div>

        <hr style={{ margin: '16px 0', border: 0, borderTop: '1px solid #f3f4f6' }} />

        <span style={label}>What to tell them</span>
        <textarea
          style={{ ...input, minHeight: 80, resize: 'vertical' }}
          value={cfg.app?.forceUpdateMessage || ''}
          onChange={(e) => patch('app.forceUpdateMessage', e.target.value)}
        />

        <div style={{ display: 'flex', gap: 12 }}>
          <div style={{ flex: 1 }}>
            <span style={label}>Oldest build allowed</span>
            <input
              style={input}
              type="number"
              value={cfg.app?.minSupportedBuild ?? 0}
              onChange={(e) => patch('app.minSupportedBuild', Number(e.target.value) || 0)}
            />
          </div>
          <div style={{ flex: 1 }}>
            <span style={label}>Newest build published</span>
            <input
              style={input}
              type="number"
              value={cfg.app?.latestBuild ?? 0}
              onChange={(e) => patch('app.latestBuild', Number(e.target.value) || 0)}
            />
          </div>
        </div>

        <div style={{ display: 'flex', gap: 12 }}>
          <div style={{ flex: 1 }}>
            <span style={label}>Android store link</span>
            <input
              style={input}
              value={cfg.app?.updateUrl?.android || ''}
              onChange={(e) => patch('app.updateUrl.android', e.target.value)}
            />
          </div>
          <div style={{ flex: 1 }}>
            <span style={label}>iPhone store link</span>
            <input
              style={input}
              value={cfg.app?.updateUrl?.ios || ''}
              onChange={(e) => patch('app.updateUrl.ios', e.target.value)}
            />
          </div>
        </div>

        <div style={{ display: 'flex', gap: 12 }}>
          <button
            type="button"
            onClick={() =>
              save(
                {
                  app: {
                    forceUpdateMessage: cfg.app?.forceUpdateMessage || '',
                    minSupportedBuild: Number(cfg.app?.minSupportedBuild) || 0,
                    latestBuild: Number(cfg.app?.latestBuild) || 0,
                    updateUrl: {
                      android: cfg.app?.updateUrl?.android || '',
                      ios: cfg.app?.updateUrl?.ios || '',
                    },
                  },
                },
                'Update settings saved.'
              )
            }
            disabled={saving}
            style={button('#0d6efd', saving)}
          >
            Save the update settings
          </button>
          <button type="button" onClick={logoutEveryone} disabled={saving} style={button('#6b7280', saving)}>
            Sign everyone out
          </button>
        </div>
      </div>

      {/* -------------------------------------------------------- support */}
      <div style={card}>
        <b style={{ fontSize: 15 }}>Support contacts</b>
        <p style={{ color: '#6b7280', fontSize: 13, margin: '4px 0 14px' }}>
          Shown on the app's Help screen. A blank field hides that contact.
        </p>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          {[
            { k: 'phone', l: 'Phone', ph: '+8801700000000' },
            { k: 'whatsapp', l: 'WhatsApp number', ph: '+8801700000000' },
            { k: 'email', l: 'Email', ph: 'support@iconmarketing.shop' },
            { k: 'hours', l: 'Hours', ph: '10am – 8pm, Saturday to Thursday' },
          ].map((f) => (
            <div key={f.k} style={{ flex: '1 1 220px' }}>
              <span style={label}>{f.l}</span>
              <input
                style={input}
                value={cfg.support?.[f.k] || ''}
                onChange={(e) => patch(`support.${f.k}`, e.target.value)}
                placeholder={f.ph}
              />
            </div>
          ))}
        </div>
        <button
          type="button"
          onClick={() =>
            save(
              {
                support: {
                  phone: cfg.support?.phone || '',
                  whatsapp: cfg.support?.whatsapp || '',
                  email: cfg.support?.email || '',
                  hours: cfg.support?.hours || '',
                },
              },
              'Support contacts saved.'
            )
          }
          disabled={saving}
          style={button('#0d6efd', saving)}
        >
          Save support contacts
        </button>
      </div>

      {/* ----------------------------------------------------- onboarding */}
      <div style={card}>
        <b style={{ fontSize: 15 }}>Welcome slides</b>
        <p style={{ color: '#6b7280', fontSize: 13, margin: '4px 0 14px' }}>
          The slides a new install sees before sign-in (up to {MAX_SLIDES}). With none here, the app shows its own three.
        </p>
        {slides.map((sl, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <div key={i} style={subCard}>
            <span style={label}>Slide {i + 1} title</span>
            <input style={input} value={sl.title || ''} onChange={(e) => setSlide(i, 'title', e.target.value)} />
            <span style={label}>Text</span>
            <textarea
              style={{ ...input, minHeight: 60, resize: 'vertical' }}
              value={sl.body || ''}
              onChange={(e) => setSlide(i, 'body', e.target.value)}
            />
            <span style={label}>Picture address (optional)</span>
            <input
              style={input}
              value={sl.image || ''}
              onChange={(e) => setSlide(i, 'image', e.target.value)}
              placeholder="https://…"
            />
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" style={smallButton} onClick={() => patch('onboarding', moveItem(slides, i, i - 1))} disabled={i === 0}>Up</button>
              <button type="button" style={smallButton} onClick={() => patch('onboarding', moveItem(slides, i, i + 1))} disabled={i === slides.length - 1}>Down</button>
              <button type="button" style={{ ...smallButton, color: '#dc2626' }} onClick={() => patch('onboarding', slides.filter((_, j) => j !== i))}>Remove</button>
            </div>
          </div>
        ))}
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          {slides.length < MAX_SLIDES ? (
            <button type="button" style={smallButton} onClick={() => patch('onboarding', [...slides, { title: '', body: '', image: '' }])}>
              + Add a slide
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => {
              const clean = slides
                .map((sl) => ({ title: String(sl.title || '').trim(), body: String(sl.body || '').trim(), image: String(sl.image || '').trim() }))
                .filter((sl) => sl.title || sl.body);
              if (!clean.length && !window.confirm('No slides left — the app will fall back to its own built-in slides. Save?')) return;
              save({ onboarding: clean }, 'Welcome slides saved.');
            }}
            disabled={saving}
            style={button('#0d6efd', saving)}
          >
            Save welcome slides
          </button>
        </div>
      </div>

      {/* ---------------------------------------------------------- tiles */}
      <div style={card}>
        <b style={{ fontSize: 15 }}>Member home buttons</b>
        <p style={{ color: '#6b7280', fontSize: 13, margin: '4px 0 14px' }}>
          The button grid on the member home screen (up to {MAX_TILES}). With none here, the app uses its own seven.
          A saved list REPLACES the app's list entirely. "Request Money" and the merchant's "Withdraw Requests" are
          always added by the app. A button whose gate (App Control key) is switched off is hidden.
        </p>
        {tiles.map((t, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <div key={i} style={subCard}>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {[
                { k: 'key', l: 'Key (unique)', ph: 'withdraw', w: '1 1 140px' },
                { k: 'label', l: 'Label', ph: 'Withdraw', w: '1 1 180px' },
                { k: 'icon', l: 'Icon', ph: 'bank-outline', w: '1 1 160px' },
                { k: 'route', l: 'Opens (app route)', ph: '/member/wallet/withdraw', w: '2 1 220px' },
                { k: 'control', l: 'App Control key', ph: 'member.wallet.withdraw', w: '2 1 220px' },
                { k: 'badge', l: 'Badge', ph: 'NEW', w: '1 1 100px' },
              ].map((f) => (
                <div key={f.k} style={{ flex: f.w }}>
                  <span style={label}>{f.l}</span>
                  <input style={input} value={t[f.k] || ''} onChange={(e) => setTile(i, f.k, e.target.value)} placeholder={f.ph} />
                </div>
              ))}
              <div style={{ flex: '1 1 120px' }}>
                <span style={label}>Colour</span>
                <div style={{ display: 'flex', gap: 6 }}>
                  <input
                    type="color"
                    style={{ width: 42, height: 40, border: '1px solid #d1d5db', borderRadius: 8, padding: 2 }}
                    value={/^#[0-9a-f]{6}$/i.test(t.color || '') ? t.color : '#2563eb'}
                    onChange={(e) => setTile(i, 'color', e.target.value)}
                  />
                  <input style={input} value={t.color || ''} onChange={(e) => setTile(i, 'color', e.target.value)} placeholder="#2563eb" />
                </div>
              </div>
              <div style={{ flex: '0 1 90px' }}>
                <span style={label}>Order</span>
                <input
                  style={input}
                  type="number"
                  value={t.order ?? ''}
                  onChange={(e) => setTile(i, 'order', e.target.value === '' ? '' : Number(e.target.value))}
                />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
              <span style={{ ...label, marginBottom: 0 }}>Roles:</span>
              {TILE_ROLES.map((r) => {
                const roles = Array.isArray(t.roles) ? t.roles : [];
                return (
                  <label key={r} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 }}>
                    <input
                      type="checkbox"
                      checked={roles.includes(r)}
                      onChange={(e) =>
                        setTile(i, 'roles', e.target.checked ? [...roles, r] : roles.filter((x) => x !== r))
                      }
                    />
                    {r}
                  </label>
                );
              })}
              <span style={{ fontSize: 12, color: '#6b7280' }}>(none ticked = everyone)</span>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" style={smallButton} onClick={() => patch('home.tiles', moveItem(tiles, i, i - 1))} disabled={i === 0}>Up</button>
              <button type="button" style={smallButton} onClick={() => patch('home.tiles', moveItem(tiles, i, i + 1))} disabled={i === tiles.length - 1}>Down</button>
              <button type="button" style={{ ...smallButton, color: '#dc2626' }} onClick={() => patch('home.tiles', tiles.filter((_, j) => j !== i))}>Remove</button>
            </div>
          </div>
        ))}
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          {tiles.length < MAX_TILES ? (
            <button
              type="button"
              style={smallButton}
              onClick={() =>
                patch('home.tiles', [
                  ...tiles,
                  { key: '', label: '', icon: 'circle-outline', route: '/member', color: '#2563eb', order: tiles.length + 1, badge: '', control: '', roles: [] },
                ])
              }
            >
              + Add a button
            </button>
          ) : null}
          {!tiles.length ? (
            <button type="button" style={smallButton} onClick={() => patch('home.tiles', APP_DEFAULT_TILES.map((t) => ({ ...t })))}>
              Start from the app's seven
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => {
              const clean = tiles
                .map((t, i) => ({
                  key: String(t.key || '').trim(),
                  label: String(t.label || '').trim(),
                  icon: String(t.icon || '').trim() || 'circle-outline',
                  route: String(t.route || '').trim() || '/member',
                  color: String(t.color || '').trim() || '#2563eb',
                  order: t.order === '' || t.order == null || Number.isNaN(Number(t.order)) ? i + 1 : Number(t.order),
                  badge: String(t.badge || '').trim(),
                  control: String(t.control || '').trim(),
                  roles: Array.isArray(t.roles) ? t.roles : [],
                }))
                .filter((t) => t.key && t.label);
              const keys = clean.map((t) => t.key);
              if (new Set(keys).size !== keys.length) {
                toast.error('Two buttons share the same key. Each key must be unique.');
                return;
              }
              if (clean.length < tiles.length && !window.confirm(`${tiles.length - clean.length} button(s) without a key or label will be dropped. Save?`)) return;
              if (!clean.length && !window.confirm('No buttons left — the app will fall back to its own seven. Save?')) return;
              save({ home: { tiles: clean } }, 'Home buttons saved.');
            }}
            disabled={saving}
            style={button('#0d6efd', saving)}
          >
            Save home buttons
          </button>
        </div>
      </div>
    </div>
  );
}
